/* ============================================================================
   PARHELIK — product quick view
   Vanilla, dependency-free, idempotent.

   Intercepts [data-ph-quick-view] clicks on product cards, fetches the
   product-quickview section via the Section Rendering API
     /products/{handle}?section_id=product-quickview
   extracts the [data-ph-quickview-content] fragment, drops it into a Parhelik
   modal (centered glass dialog + overlay), then boots window.PHProduct on it so
   the variant picker / swatches / gallery / qty stepper / AJAX add-to-cart all
   work exactly like the PDP. window.PHCart intercepts the add-to-cart form, so a
   successful add opens the cart drawer — we close the quick view when that
   happens. Closes on overlay click, the close button, and Esc.

   The trigger element carries the product handle in any of:
     data-ph-quick-view="{handle}" · data-product-handle · data-handle
   or an href to /products/{handle}.

   Public API: window.PHQuickView.open(handle), .close()
   ========================================================================== */
(function () {
  'use strict';

  if (window.PHQuickView && window.PHQuickView._installed) return;

  var SECTION_ID = 'product-quickview';
  var MODAL_ID = 'ph-quickview-modal';
  var CONTENT_SEL = '[data-ph-quickview-content]';
  var TRIGGER_SEL = '[data-ph-quick-view]';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ---- modal singleton -------------------------------------------------- */
  var modalEl = null;
  var bodyEl = null;
  var isOpen = false;
  var lastTrigger = null;

  function buildModal() {
    if (modalEl) return modalEl;

    var overlay = document.createElement('div');
    overlay.id = MODAL_ID;
    overlay.className = 'ph-quickview';
    overlay.setAttribute('aria-hidden', 'true');

    overlay.innerHTML =
      '<div class="ph-quickview__overlay" data-ph-quickview-close tabindex="-1"></div>' +
      '<div class="ph-quickview__dialog" role="dialog" aria-modal="true" aria-label="Product quick view">' +
        '<button type="button" class="ph-quickview__close" data-ph-quickview-close aria-label="Close">' +
          '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true">' +
            '<path d="M5 5l14 14M19 5L5 19"></path>' +
          '</svg>' +
        '</button>' +
        '<div class="ph-quickview__scroll">' +
          '<div class="ph-quickview__body" data-ph-quickview-body>' +
            '<div class="ph-quickview__loading" aria-live="polite">' +
              '<span class="ph-quickview__spinner" aria-hidden="true"></span>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>';

    document.body.appendChild(overlay);
    modalEl = overlay;
    bodyEl = overlay.querySelector('[data-ph-quickview-body]');
    return modalEl;
  }

  function setLoading(state) {
    if (!modalEl) return;
    modalEl.classList.toggle('is-loading', !!state);
  }

  function showModal() {
    buildModal();
    modalEl.classList.add('is-open');
    modalEl.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('ph-scroll-lock');
    isOpen = true;
  }

  function close() {
    if (!modalEl) return;
    modalEl.classList.remove('is-open');
    modalEl.setAttribute('aria-hidden', 'true');
    // Only release the global scroll lock if the cart drawer isn't holding it.
    var cart = document.querySelector('#cart-drawer.is-open');
    if (!cart) document.documentElement.classList.remove('ph-scroll-lock');
    isOpen = false;

    // tear down any product instance mounted inside the modal
    if (bodyEl && window.PHProduct && typeof window.PHProduct.unmount === 'function') {
      Array.prototype.slice.call(bodyEl.querySelectorAll('[data-ph-product]')).forEach(function (el) {
        try { window.PHProduct.unmount(el); } catch (e) { /* noop */ }
      });
    }
    if (bodyEl) bodyEl.innerHTML = '';

    if (lastTrigger) {
      lastTrigger.classList.remove('is-loading');
      try { lastTrigger.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    }
  }

  /* ---- resolve a product handle from a trigger -------------------------- */
  function handleFromTrigger(el) {
    if (!el) return '';
    var h = el.getAttribute('data-ph-quick-view');
    if (h) return h;
    h = el.getAttribute('data-product-handle') || el.getAttribute('data-handle');
    if (h) return h;
    var href = el.getAttribute('href') || (el.dataset && el.dataset.productUrl) || '';
    var m = href.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  /* ---- fetch + inject --------------------------------------------------- */
  function fetchSection(handle) {
    var base = root().replace(/\/$/, '');
    var url = base + '/products/' + encodeURIComponent(handle) + '?section_id=' + SECTION_ID;
    return fetch(url, { headers: { Accept: 'text/html' } }).then(function (r) {
      if (!r.ok) throw new Error('quick view HTTP ' + r.status);
      return r.text();
    });
  }

  function inject(html) {
    if (!bodyEl) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');

    // Prefer the scoped section wrapper so the section's <style> travels with it;
    // fall back to the content fragment alone.
    var content =
      doc.querySelector('.shopify-section') ||
      doc.querySelector(CONTENT_SEL);

    if (!content) {
      bodyEl.innerHTML = '<p class="ph-quickview__error ph-mono">Unable to load product.</p>';
      return;
    }

    bodyEl.innerHTML = '';
    bodyEl.appendChild(document.importNode(content, true));

    // Re-execute any inline <script> the section emitted (importNode/innerHTML
    // do not run them). Defensive — the Parhelik quick view ships no inline JS,
    // but ported markup might.
    Array.prototype.slice.call(bodyEl.querySelectorAll('script')).forEach(function (old) {
      var s = document.createElement('script');
      Array.prototype.slice.call(old.attributes).forEach(function (a) { s.setAttribute(a.name, a.value); });
      s.appendChild(document.createTextNode(old.textContent || ''));
      old.parentNode.replaceChild(s, old);
    });

    // Boot Parhelik product interactivity on the freshly injected root(s).
    if (window.PHProduct && typeof window.PHProduct.init === 'function') {
      window.PHProduct.init(bodyEl);
    }
    // Wire reveal/parallax hooks the gallery ghost relies on.
    if (window.Parhelik && typeof window.Parhelik.observeAll === 'function') {
      try { window.Parhelik.observeAll(bodyEl); } catch (e) { /* noop */ }
    }

    bodyEl.dispatchEvent(new CustomEvent('ph:quickview:loaded', { bubbles: true, detail: { body: bodyEl } }));
  }

  /* ---- open ------------------------------------------------------------- */
  var isFetching = false;

  function open(handle, trigger) {
    if (!handle || isFetching) return;
    lastTrigger = trigger || null;
    isFetching = true;
    if (lastTrigger) lastTrigger.classList.add('is-loading');

    showModal();
    setLoading(true);

    fetchSection(handle)
      .then(function (html) {
        inject(html);
      })
      .catch(function (err) {
        console.error('[PHQuickView] load failed', err);
        if (bodyEl) bodyEl.innerHTML = '<p class="ph-quickview__error ph-mono">Unable to load product. <a href="' + root().replace(/\/$/, '') + '/products/' + encodeURIComponent(handle) + '">View product</a></p>';
      })
      .then(function () {
        setLoading(false);
        isFetching = false;
        if (lastTrigger) lastTrigger.classList.remove('is-loading');
      });
  }

  /* ---- delegated events ------------------------------------------------- */
  function onClick(e) {
    var trigger = e.target.closest && e.target.closest(TRIGGER_SEL);
    if (trigger) {
      var handle = handleFromTrigger(trigger);
      if (handle) {
        e.preventDefault();
        open(handle, trigger);
      }
      return;
    }
    if (e.target.closest && e.target.closest('[data-ph-quickview-close]')) {
      e.preventDefault();
      close();
    }
  }

  function onKeydown(e) {
    if ((e.key === 'Escape' || e.key === 'Esc') && isOpen) {
      close();
    }
  }

  function bind() {
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);
    // When an add-to-cart succeeds the cart drawer opens; close quick view so
    // the shopper sees their bag (mirrors minimog's cartUpdate -> modal.close).
    document.addEventListener('submit', function (e) {
      var form = e.target.closest && e.target.closest('[data-ph-add-to-cart]');
      if (form && bodyEl && bodyEl.contains(form)) {
        // Defer so PHCart finishes its add + drawer open first.
        setTimeout(function () { if (isOpen) close(); }, 400);
      }
    }, false);
  }

  /* ---- public API ------------------------------------------------------- */
  window.PHQuickView = {
    _installed: true,
    open: open,
    close: close
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
